import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Collapse,
  LinearProgress,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Link,
  useTheme,
  Checkbox,
  ToggleButton,
  Tooltip
} from '@mui/material';
import Button from '../../UIKit/Button';
import ToggleButtonGroup from '../../UIKit/ToggleButtonGroup';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../../../services/api';
import SearchInput from '../../UIKit/SearchInput';
import UserPagination from '../users/UserPagination';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ─── Detail Dialog ────────────────────────────────────────────────────────────

const DetailDialog = ({ open, onClose, userId, courseId, courseName, courseSlug, userName, t }) => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !userId || !courseId) return;
    setLoading(true);
    setError(null);
    setData(null);
    usersAPI.getCourseProgressDetail(userId, courseId)
      .then(res => setData(res.data))
      .catch(() => setError(t('admin:courseProgress.detail.loadError')))
      .finally(() => setLoading(false));
  }, [open, userId, courseId, t]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            borderRadius: 3,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            backgroundColor: theme.palette.background.paper,
            maxHeight: '90vh',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box>
          <Typography variant="h6" component="div" fontWeight={600}>
            {courseName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {userName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            mt: 0.5,
            color: theme.palette.text.secondary,
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 3, px: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {data && (
          <>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              {t('admin:courseProgress.detail.lessonsSection')}
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: theme.palette.action.hover } }}>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('admin:courseProgress.detail.lessonTitle')}</TableCell>
                    <TableCell>{t('admin:courseProgress.detail.contentType')}</TableCell>
                    <TableCell>{t('admin:courseProgress.detail.lastAccess')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lessons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      </TableCell>
                    </TableRow>
                  ) : data.lessons.map((lesson) => (
                    <TableRow key={lesson._id} hover>
                      <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                        {lesson.completed
                          ? <CheckCircleIcon fontSize="small" color="success" />
                          : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                      </TableCell>
                      <TableCell>
                        {courseSlug && lesson.moduleSlug && lesson.slug ? (
                          <Link
                            href={`/courses/${courseSlug}/modules/${lesson.moduleSlug}/lessons/${lesson.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            variant="body2"
                            color="primary"
                          >
                            {lesson.title}
                          </Link>
                        ) : (
                          <Typography variant="body2">{lesson.title}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={lesson.contentType} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(lesson.lastAccessedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {data.tests.length > 0 && (
              <>
                <Divider sx={{ mb: 2.5 }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  {t('admin:courseProgress.detail.testsSection')}
                </Typography>
                {data.tests.map((test) => (
                  <Paper
                    key={test._id}
                    variant="outlined"
                    sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: theme.palette.background.default }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      {test.passed
                        ? <CheckCircleIcon fontSize="small" color="success" />
                        : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                      <Typography variant="body2" fontWeight={600}>{test.title}</Typography>
                      <Chip
                        label={`${t('admin:courseProgress.detail.passingScore')}: ${test.passingScore}%`}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 'auto', fontSize: '0.7rem' }}
                      />
                    </Box>
                    {test.attempts.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('admin:courseProgress.detail.notStarted')}
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: theme.palette.action.hover } }}>
                              <TableCell>{t('admin:courseProgress.detail.attempt')}</TableCell>
                              <TableCell align="center">{t('admin:courseProgress.detail.score')}</TableCell>
                              <TableCell align="center">{t('admin:courseProgress.detail.correct')}</TableCell>
                              <TableCell align="center">{t('admin:courseProgress.detail.result')}</TableCell>
                              <TableCell>{t('admin:courseProgress.detail.date')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {test.attempts.map((attempt, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">#{idx + 1}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" fontWeight={600}>
                                    {Math.round(attempt.score)}%
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {attempt.correctAnswers}/{attempt.totalQuestions}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={attempt.passed
                                      ? t('admin:courseProgress.detail.passed')
                                      : t('admin:courseProgress.detail.failed')}
                                    size="small"
                                    color={attempt.passed ? 'success' : 'error'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatDate(attempt.completedAt)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Course summary row (inside expanded user) ────────────────────────────────

const CourseRow = ({ user, checked, onToggle, t }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const name = user.userMeta?.fullName || user.userMeta?.email || '—';
  const avatar = user.userMeta?.avatar;
  const email = user.userMeta?.email || '—';

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding="checkbox">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              size="small"
              checked={checked}
              onChange={() => onToggle(user._id)}
            />
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={avatar} sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
              {name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" fontWeight={500}>{name}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">{email}</Typography>
        </TableCell>
        <TableCell align="center">
          <Chip label={user.enrolledCoursesCount} size="small" color="primary" variant="outlined" />
        </TableCell>
        <TableCell align="center">
          <Chip
            label={user.completedCoursesCount}
            size="small"
            color={user.completedCoursesCount > 0 ? 'success' : 'default'}
            variant="outlined"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                {t('admin:courseProgress.detail.title', { name })}
              </Typography>
              {user.courses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                  {t('admin:courseProgress.detail.noData')}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('admin:courseProgress.detail.course')}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>{t('admin:courseProgress.detail.progress')}</TableCell>
                      <TableCell align="center">{t('admin:courseProgress.detail.lessonsCompleted')}</TableCell>
                      <TableCell align="center">{t('admin:courseProgress.detail.testsCompleted')}</TableCell>
                      <TableCell>{t('admin:courseProgress.detail.lastLesson')}</TableCell>
                      <TableCell>{t('admin:courseProgress.detail.lastAccess')}</TableCell>
                      <TableCell padding="checkbox" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {user.courses.map((course) => (
                      <TableRow key={course.courseId}>
                        <TableCell>
                          {course.courseSlug ? (
                            <Link
                              href={`/courses/${course.courseSlug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              variant="body2"
                              fontWeight={500}
                              color="primary"
                            >
                              {course.courseTitle}
                            </Link>
                          ) : (
                            <Typography variant="body2" fontWeight={500}>
                              {course.courseTitle}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={course.progress}
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32 }}>
                              {course.progress}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {course.completedLessons}/{course.totalLessons}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {course.totalTests > 0
                              ? `${course.completedTests}/${course.totalTests}`
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {course.lastLesson
                              ? course.lastLesson.title
                              : t('admin:courseProgress.detail.notStarted')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(course.lastAccessedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => setDetail({ courseId: course.courseId, courseName: course.courseTitle, courseSlug: course.courseSlug })}
                            title={t('admin:courseProgress.detail.openDetail')}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {detail && (
        <DetailDialog
          open={!!detail}
          onClose={() => setDetail(null)}
          userId={user._id}
          courseId={detail.courseId}
          courseName={detail.courseName}
          courseSlug={detail.courseSlug}
          userName={name}
          t={t}
        />
      )}
    </>
  );
};

// ─── Sort button ──────────────────────────────────────────────────────────────

const SortButton = ({ label, field, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  const icon = active
    ? (sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="inherit" /> : <ArrowDownwardIcon fontSize="inherit" />)
    : null;

  return (
    <ToggleButton
      value={field}
      selected={active}
      onClick={() => onSort(field)}
      sx={{ gap: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
    >
      {label}
      {icon}
    </ToggleButton>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const CourseProgressDashboard = () => {
  const { t } = useTranslation(['admin']);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersAPI.getCourseProgress({
        page: pagination.page,
        limit: pagination.limit,
        search,
        sortBy,
        sortOrder
      });
      setUsers(response.data.users || []);
      const p = response.data.pagination || {};
      setPagination(prev => ({
        ...prev,
        total: p.total || 0,
        pages: p.pages || 1
      }));
    } catch (err) {
      setError(t('admin:courseProgress.loadError'));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, sortBy, sortOrder, t]);

  useEffect(() => {
    const timer = setTimeout(fetchData, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchData, search]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (_, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value, 10), page: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'createdAt' ? 'desc' : 'asc');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleToggleUser = (userId) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const visibleIds = users.map(u => u._id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedUsers.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedUsers.has(id));

  const handleToggleAll = () => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleClearSelection = () => setSelectedUsers(new Set());

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await usersAPI.exportCourseProgress({
        userIds: [...selectedUsers].join(',')
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'course-progress.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user can retry
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('admin:courseProgress.title')}
      </Typography>

      {/* Toolbar */}
      <Box sx={{ mb: 3 }}>
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder={t('admin:courseProgress.searchPlaceholder')}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t('admin:courseProgress.sortBy')}:
            </Typography>
            <ToggleButtonGroup size="small">
              <SortButton
                label={t('admin:courseProgress.sortName')}
                field="name"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              <SortButton
                label={t('admin:courseProgress.sortDate')}
                field="createdAt"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedUsers.size > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClearSelection}
              >
                {t('admin:courseProgress.clearSelection')}
              </Button>
            )}
            <Tooltip title={selectedUsers.size === 0 ? t('admin:courseProgress.selectToExport') : ''}>
              <span>
                <Button
                  variant={selectedUsers.size > 0 ? 'contained' : 'outlined'}
                  color="primary"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />}
                  disabled={selectedUsers.size === 0 || exporting}
                  onClick={handleExport}
                >
                  {exporting
                    ? t('admin:courseProgress.exportLoading')
                    : selectedUsers.size > 0
                      ? t('admin:courseProgress.exportSelected', { count: selectedUsers.size })
                      : t('admin:courseProgress.export')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  onChange={handleToggleAll}
                  disabled={users.length === 0}
                />
              </TableCell>
              <TableCell>{t('admin:courseProgress.columns.user')}</TableCell>
              <TableCell>{t('admin:courseProgress.columns.email')}</TableCell>
              <TableCell align="center">{t('admin:courseProgress.columns.enrolled')}</TableCell>
              <TableCell align="center">{t('admin:courseProgress.columns.completed')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {t('admin:courseProgress.noUsers')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <CourseRow
                  key={user._id}
                  user={user}
                  checked={selectedUsers.has(user._id)}
                  onToggle={handleToggleUser}
                  t={t}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UserPagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />
    </Box>
  );
};

export default CourseProgressDashboard;
